import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { errText } from "../biz/i18n";
import { Card, Empty, btnSoft, inp, Field } from "../biz/ui";
import { useLabT } from "./i18n";
import { Check } from "./fields";
import { QuestionBuilder, Collapse } from "./QuestionBuilder";

const AnswerRow = ({ k, a, stage, lang }) => {
  const legacy = typeof a !== "object" || a === null;
  const q = legacy ? stage.questions?.[k] || {} : { en: a.q_en, ar: a.q_ar };
  const val = legacy ? String(a) : a.text || (a.selected || []).map((s) => (lang === "ar" ? s.ar : s.en)).join("، ");
  return (
    <div className="text-sm">
      <p className="text-[#8A8173] text-xs">{(lang === "ar" ? q.ar : q.en) || q.en || k}</p>
      {val && <p className="font-medium">{val}</p>}
      {!legacy && a.comment && <p className="text-xs text-[#6B645A] italic mt-0.5">“{a.comment}”</p>}
    </div>
  );
};

const Answers = ({ stage, lang }) => (
  <div className="space-y-2.5 mt-2">
    {Object.entries(stage.answers || {}).map(([k, a]) => <AnswerRow key={k} k={k} a={a} stage={stage} lang={lang} />)}
  </div>
);

const Stage = ({ ev, n, t, lang }) => {
  const st = ev[`stage${n}`];
  if (!st) return null;
  const draft = st.submitted === false;
  return (
    <div className="mt-3">
      <p className="font-semibold text-sm">{n === 1 ? t("first_impression") : t("full_evaluation")} <span className="text-xs font-normal text-[#8A8173]">· {st.at?.slice(0, 10)}{draft ? ` · ${t("s_draft")}` : ""}</span></p>
      <Answers stage={st} lang={lang} />
    </div>
  );
};

const StageToggles = ({ settings, reload, t }) => {
  const set = async (k, v) => { try { await api.put("/admin/lab/settings", { [k]: v }); reload(); } catch (e) { toast.error(errText(e)); } };
  return (
    <div className="flex flex-wrap gap-5">
      <Check label={t("stage1_enabled")} checked={settings.stage1_enabled} onChange={(v) => set("stage1_enabled", v)} testid="lab-stage1-toggle" />
      <Check label={t("stage2_enabled")} checked={settings.stage2_enabled} onChange={(v) => set("stage2_enabled", v)} testid="lab-stage2-toggle" />
    </div>
  );
};

const EvalCard = ({ ev, t, lang, onToggle }) => (
  <Card testid={`lab-eval-${ev.id}`}>
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <p className="text-sm"><b dir="ltr">{ev.experiment_code}</b> · {ev.name || ev.display_name} <span className="text-xs text-[#8A8173]" dir="ltr">· {ev.email}</span></p>
      <div className="flex items-center gap-2">
        <span className="text-xs text-[#8A8173]">{ev.allow_publish ? t("consent") : t("no_consent")}</span>
        {ev.allow_publish && <button onClick={() => onToggle(ev)} className={btnSoft} data-testid={`lab-eval-pub-${ev.id}`}>{ev.published ? t("make_private") : t("make_public")}</button>}
      </div>
    </div>
    <Stage ev={ev} n={1} t={t} lang={lang} />
    <Stage ev={ev} n={2} t={t} lang={lang} />
  </Card>
);

export function LabEvaluations({ data, reload }) {
  const { t, lang } = useLabT();
  const [eid, setEid] = useState("");
  const [email, setEmail] = useState("");
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    const q = new URLSearchParams({ experiment_id: eid, email: email.trim() });
    api.get(`/admin/lab/evaluations?${q}`).then((r) => setRows(r.data)).catch((e) => toast.error(errText(e)));
  }, [eid, email]);
  useEffect(() => { const h = setTimeout(load, 300); return () => clearTimeout(h); }, [load]);
  const toggle = async (ev) => {
    try { await api.put(`/admin/lab/evaluations/${ev.id}`, { published: !ev.published }); load(); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="bg-white border border-[#EEE5D8] rounded-2xl px-4 py-3.5"><p className="text-sm font-semibold mb-2">{t("stages")}</p><StageToggles settings={data.settings} reload={reload} t={t} /></div>
      <Collapse title={t("eval_questions")} testid="lab-eval-questions">
        <div className="space-y-4">
          <QuestionBuilder key={JSON.stringify(data.settings.questions).length} field="questions" initial={data.settings.questions} withStage reload={reload} testid="qb-eval" /></div>
      </Collapse>
      <Collapse title={t("app_questions")} testid="lab-app-questions">
        <QuestionBuilder key={JSON.stringify(data.settings.application_questions).length} field="application_questions" initial={data.settings.application_questions} reload={reload} testid="qb-app" />
      </Collapse>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label={t("choose_experiment")}>
          <select value={eid} onChange={(e) => setEid(e.target.value)} data-testid="lab-eval-exp" className={inp}>
            <option value="">{t("all")}</option>
            {data.experiments.map((e) => <option key={e.id} value={e.id}>{e.code} · {(lang === "ar" ? e.name_ar : e.name_en) || e.name_en}</option>)}
          </select>
        </Field>
        <Field label={t("filter_participant")}><input dir="ltr" value={email} onChange={(e) => setEmail(e.target.value)} className={inp} data-testid="lab-eval-email" /></Field>
      </div>
      {!rows.length && <Empty>{t("no_items")}</Empty>}
      {rows.map((ev) => (
        <EvalCard key={ev.id} ev={ev} t={t} lang={lang} onToggle={toggle} />
      ))}
    </div>
  );
}
